import { createElement } from 'react'

import * as Sentry from '@sentry/react'

import { initSentry as initSharedSentry } from '@reveni-io/easy-peasy/sentry'

import VariableLabel from '../components/VariableLabel/VariableLabel'
import {
  baseReasonsByLanguage,
  DEFAULT_SENTRY_TRACES_SAMPLE_RATE,
  emailVariables,
  emailVariablesSections,
  phoneAttributesByEventName,
} from '../constants'

export const getFilenameFromUrl = url => {
  if (url) {
    const pattern = /\/([^/?]+)(?:\?.*)?$/
    const matches = url.match(pattern)
    return matches.length > 0 ? matches[1] : null
  }
  return null
}

export const hasAllProvincesSelected = (regionId, numberOfProvincesSelected, regions) => {
  const selectedRegion = regions.find(region => region.id === regionId)
  return selectedRegion?.provinces?.length === numberOfProvincesSelected
}

export const getRegionsIds = (deliveryMethodRegions, regions) => {
  return deliveryMethodRegions.reduce((prev, next) => {
    if (!hasAllProvincesSelected(next.value || next.id, next?.provinces?.length || next?.children?.length, regions)) {
      if (next?.children?.length) {
        return [...prev, ...next.children.map(c => c.value)]
      } else if (next?.provinces?.length) {
        return [...prev, ...next.provinces.map(c => c.id)]
      }
    }
    return [...prev, next.value || next.id]
  }, [])
}

export const transformRegionsForMultiSelect = regions => {
  let newRegions = []

  if (regions?.length) {
    for (let region of regions) {
      if (region?.provinces?.length) {
        newRegions = [...newRegions, ...region.provinces]
      } else {
        newRegions.push(region)
      }
    }
  }

  return newRegions
}

export const getSelectedRegions = (regionsIds = [], regions = []) => {
  let selectedRegions = []

  regions.forEach(region => {
    if (regionsIds?.includes(region.id)) {
      selectedRegions.push(region)
    } else {
      const provinces = region?.provinces?.filter(p => regionsIds?.includes(p.id))

      if (provinces.length) {
        selectedRegions.push({ ...region, provinces })
      }
    }
  })

  return selectedRegions || []
}

export const getSelectedRegionsForMultiSelect = (regionsIds, regions) => {
  if (!regionsIds?.length || !regions?.length) return []

  return regions.flatMap(region => {
    if (regionsIds.includes(region.id)) return { id: region.id, name: region.name }

    if (!region.provinces?.length) return []

    const selectedProvinces = region.provinces.filter(province => regionsIds.includes(province.id))

    return selectedProvinces.map(province => ({ id: province.id, name: province.name }))
  })
}

export const parsePercentage = (value, language) => {
  const formatter = new Intl.NumberFormat(language)
  return formatter.format(value)
}

export const getSelectedStore = (stores, defaultStoreId) => {
  const searchParams = new URLSearchParams(window.location.search)
  const storeIdQueryParam = searchParams.get('store_id')
  const storeIdLocalStorage =
    localStorage?.selectedStore && localStorage?.selectedStore !== 'undefined'
      ? JSON.parse(localStorage.selectedStore)?.id
      : null

  const selectedStoreId = localStorage.forceChangeStore
    ? storeIdLocalStorage
    : defaultStoreId || storeIdQueryParam || storeIdLocalStorage
  const selectedStore = stores.find(store => store.id === selectedStoreId)

  localStorage.removeItem('forceChangeStore')
  return selectedStore || stores[0]
}

export const flattenReturnPortalsFromStores = (stores = []) => {
  return stores.flatMap(store => (store.portals || []).map(portal => ({ ...portal, store })))
}

export const getSelectedReturnPortal = (returnPortals, defaultPortalId) => {
  const searchParams = new URLSearchParams(window.location.search)
  const portalIdQueryParam = searchParams.get('portal_id')
  const portalIdLocalStorage =
    localStorage?.selectedReturnPortal && localStorage?.selectedReturnPortal !== 'undefined'
      ? JSON.parse(localStorage.selectedReturnPortal)?.id
      : null

  const selectedPortalId = localStorage.forceChangeReturnPortal
    ? portalIdLocalStorage
    : defaultPortalId || portalIdQueryParam || portalIdLocalStorage
  const selectedReturnPortal = returnPortals.find(portal => portal.id === selectedPortalId)

  localStorage.removeItem('forceChangeReturnPortal')
  return selectedReturnPortal || returnPortals[0]
}

export const transformMethodsByRegions = (methods = [], regions = []) => {
  return methods.map(method => {
    method.regions = method?.regions?.map(methodRegion => {
      if (!methodRegion?.provinces?.length) {
        methodRegion.provinces = regions.find(region => region.id === methodRegion.id)?.provinces
      }
      return methodRegion
    })

    return method
  })
}

const isValidSampleRate = rate => Number.isFinite(rate) && rate >= 0 && rate <= 1

export const getSentryTracesSampleRate = () => {
  const value = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE?.trim()
  const rate = value ? Number(value) : Number.NaN

  return isValidSampleRate(rate) ? rate : DEFAULT_SENTRY_TRACES_SAMPLE_RATE
}

export const initSentry = () => {
  if (import.meta.env.VITE_SENTRY_ENVIRONMENT) {
    initSharedSentry({
      dsn: import.meta.env.VITE_SENTRY_URL,
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
      release: VERSION,
      tracesSampleRate: getSentryTracesSampleRate(),
    })
  }
}

const getStoreWithoutSecrets = store => {
  if (!store) return store

  const { private_api_token: _, ...storeWithoutSecrets } = store

  return storeWithoutSecrets
}

export const addSentryContext = store => {
  if (import.meta.env.VITE_SENTRY_ENVIRONMENT) {
    Sentry.setExtra('store', getStoreWithoutSecrets(store))
  }
}

export const decipherJwt = jwt => {
  const payload = jwt?.split('.')[1]
  if (payload) {
    return JSON.parse(atob(payload))
  }
}

const getStoredStoreWithoutSecrets = storedStore => {
  if (!storedStore || storedStore === 'undefined') return storedStore

  return getStoreWithoutSecrets(JSON.parse(storedStore))
}

export const getLocalStorageForSentry = () => {
  const { jwt, refresh: _, selectedStore, ...localStorageWithoutSecrets } = { ...localStorage }

  return {
    ...localStorageWithoutSecrets,
    jwt: decipherJwt(jwt),
    selectedStore: getStoredStoreWithoutSecrets(selectedStore),
  }
}

export const getCurrencySymbolByCode = (code, currencies = []) => {
  return currencies?.find(currency => currency.code === code)?.symbol || ''
}

export const decodeAndDownloadFile = ({ encodedFile, fileName, type, callback }) => {
  const binaryString = atob(encodedFile)

  if (binaryString.length > 0) {
    const byteArray = new Uint8Array(binaryString.length)

    for (let i = 0; i < binaryString.length; i++) {
      byteArray[i] = binaryString.charCodeAt(i)
    }

    const blob = new Blob([byteArray], { type })

    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')

    a.href = url
    a.download = fileName
    a.click()

    URL.revokeObjectURL(url)

    if (typeof callback === 'function') {
      callback()
    }
  }
}

export const getAvailableEmailVariables = (emailTypes, triggerTypes = []) => {
  return emailVariables.filter(v => {
    const isAvailableForEmailType = v.availableFor.some(type => emailTypes.includes(type))
    const isAvailableForTriggers = !v.onlyForTrigger || triggerTypes.includes(v.onlyForTrigger)

    return isAvailableForEmailType && isAvailableForTriggers
  })
}

export const getAvailableEmailVariablesSections = emailTypes => {
  return emailVariablesSections.filter(vs => vs.availableFor.some(type => emailTypes.includes(type)))
}

export const getEmailVariables = variables => {
  return variables?.map(v => ({
    ...v,
    label: createElement(VariableLabel, { type: v.type, label: v.label, variable: v.variable }),
    value: v.variable.replace('{{', ''),
  }))
}

export const isHtmlContentEmpty = html => {
  if (!html) return true
  if (/<img\b/i.test(html)) return false

  const textContent = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, '')
    .trim()

  return textContent === ''
}

export const groupRegionsByContinent = regions => {
  let regionsGroupedByContinent = []
  for (let region of regions) {
    const indexContinent = regionsGroupedByContinent.findIndex(r => r.id === region?.continent?.code)

    if (~indexContinent) {
      regionsGroupedByContinent[indexContinent].provinces.push(region)
    } else {
      regionsGroupedByContinent.push({
        id: region?.continent?.code,
        name: region?.continent?.name,
        provinces: [region],
      })
    }
  }

  return regionsGroupedByContinent
}

export const parseComboBoxValue = value => (value === 'null' ? null : value)

export const getReasonLabel = (reason, language) => {
  return reason?.display[language] || baseReasonsByLanguage?.[reason?.key]?.[language]
}

export const getReasonTypeMenuItems = language => {
  return Object.entries(baseReasonsByLanguage).map(([key, translations]) => ({
    label: translations[language],
    value: key,
  }))
}

export const getReasonMenuItems = (returnReasons, language) => {
  return returnReasons?.map(r => ({ label: getReasonLabel(r, language), value: r.id }))
}

const mapSubreasonMenuItem = (subreason, language) => {
  return { id: subreason.id, label: subreason.display[language] || subreason.internal_name }
}

export const getHierarchicalReasonMenuItems = (returnReasons, language) => {
  return returnReasons?.map(reason => {
    const item = { id: reason.id, label: getReasonLabel(reason, language) }

    if (reason.subreasons?.length) {
      item.children = reason.subreasons.map(subreason => mapSubreasonMenuItem(subreason, language))
    }

    return item
  })
}

export const openInNewTab = url => {
  if (url) window.open(url, '_blank', 'noreferrer')
}

export const updateAddressField = (address, name, value) => {
  const phoneAttribute = phoneAttributesByEventName[name]

  if (phoneAttribute) {
    return { ...address, phone: { ...address?.phone, [phoneAttribute]: value } }
  }

  if (name === 'province') {
    return { ...address, province: { ...address?.province, code: value } }
  }

  return { ...address, [name]: value }
}
